"use client"

// src/components/graficas/GraficasGastos.js
import { useMemo } from "react"
import { Box, Typography, Paper, Grid } from "@mui/material"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts"

const COLORS = ["#5b5b5f", "rgb(244, 177, 14)", "#82ca9d", "#ffc658", "#ff7300", "#8884d8", "#00ff00", "#ff00ff"]

export default function GraficasGastos({ gastos, subCategories, filtros }) {
  // Procesar datos para gráfica por CATEGORÍA
  const datosPorCategoria = useMemo(() => {
    const agrupados = {}

    gastos.forEach((gasto) => {
      const nombreCategoria = gasto.categoriaDesc || "Sin categoría"

      if (!agrupados[nombreCategoria]) {
        agrupados[nombreCategoria] = {
          nombre: nombreCategoria,
          total: 0,
          cantidad: 0,
        }
      }

      agrupados[nombreCategoria].total += Number.parseFloat(gasto.montoTotal || 0)
      agrupados[nombreCategoria].cantidad += 1
    })

    return Object.values(agrupados).sort((a, b) => b.total - a.total)
  }, [gastos])

  // Procesar datos para gráfica por SUBCATEGORÍA
  const datosPorSubcategoria = useMemo(() => {
    const agrupados = {}

    gastos.forEach((gasto) => {
      const nombreSubcat = gasto.subCategoriaDesc || "Sin subcategoría"

      if (!agrupados[nombreSubcat]) {
        agrupados[nombreSubcat] = {
          nombre: nombreSubcat,
          total: 0,
          cantidad: 0,
          categoria: gasto.categoriaDesc || "Sin categoría",
        }
      }

      agrupados[nombreSubcat].total += Number.parseFloat(gasto.montoTotal || 0)
      agrupados[nombreSubcat].cantidad += 1
    })

    return Object.values(agrupados).sort((a, b) => b.total - a.total)
  }, [gastos])

  // Procesar datos para gráfica por entidad
  const datosPorEntidad = useMemo(() => {
    const agrupados = {}

    gastos.forEach((gasto) => {
      const nombreEntidad = gasto.entidad?.descripcion || "Sin entidad"

      if (!agrupados[nombreEntidad]) {
        agrupados[nombreEntidad] = {
          nombre: nombreEntidad,
          total: 0,
          cantidad: 0,
        }
      }

      agrupados[nombreEntidad].total += Number.parseFloat(gasto.montoTotal || 0)
      agrupados[nombreEntidad].cantidad += 1
    })

    return Object.values(agrupados)
      .sort((a, b) => b.total - a.total)
      .slice(0, 8) // Top 8 entidades
  }, [gastos])

  // Procesar datos para gráfica temporal
  const datosTemporal = useMemo(() => {
    const agrupados = {}

    gastos.forEach((gasto) => {
      const fecha = new Date(gasto.fecha)
      const fechaKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, "0")}-${String(fecha.getDate()).padStart(2, "0")}`

      if (!agrupados[fechaKey]) {
        agrupados[fechaKey] = {
          fecha: fechaKey,
          total: 0,
          cantidad: 0,
        }
      }

      agrupados[fechaKey].total += Number.parseFloat(gasto.montoTotal || 0)
      agrupados[fechaKey].cantidad += 1
    })

    return Object.values(agrupados).sort((a, b) => new Date(a.fecha) - new Date(b.fecha))
  }, [gastos])

  // Comparación Categoría vs Subcategoría
  const comparacionCategoriaSubcategoria = useMemo(() => {
    const datos = []

    // Agrupar por categoría y sus subcategorías
    const categorias = {}
    gastos.forEach((gasto) => {
      const categoria = gasto.categoriaDesc || "Sin categoría"
      const subcategoria = gasto.subCategoriaDesc || "Sin subcategoría"

      if (!categorias[categoria]) {
        categorias[categoria] = {}
      }

      if (!categorias[categoria][subcategoria]) {
        categorias[categoria][subcategoria] = 0
      }

      categorias[categoria][subcategoria] += Number.parseFloat(gasto.montoTotal || 0)
    })

    // Convertir a formato para gráfica
    Object.entries(categorias).forEach(([categoria, subcategorias]) => {
      Object.entries(subcategorias).forEach(([subcategoria, monto]) => {
        datos.push({
          categoria,
          subcategoria,
          monto,
          nombre: `${categoria} - ${subcategoria}`,
        })
      })
    })

    return datos.sort((a, b) => b.monto - a.monto).slice(0, 10) // Top 10
  }, [gastos])

  // Calcular estadísticas generales
  const estadisticas = useMemo(() => {
    const totalGastos = gastos.reduce((sum, gasto) => sum + Number.parseFloat(gasto.montoTotal || 0), 0)
    const promedioGasto = gastos.length > 0 ? totalGastos / gastos.length : 0
    const gastoMaximo = Math.max(...gastos.map((g) => Number.parseFloat(g.montoTotal || 0)))
    const gastoMinimo = Math.min(...gastos.map((g) => Number.parseFloat(g.montoTotal || 0)))

    return {
      total: totalGastos,
      promedio: promedioGasto,
      maximo: gastoMaximo,
      minimo: gastoMinimo,
      cantidad: gastos.length,
      categorias: datosPorCategoria.length,
      subcategorias: datosPorSubcategoria.length,
    }
  }, [gastos, datosPorCategoria, datosPorSubcategoria])

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <Box>
      {/* Estadísticas generales */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#f5f5f5" }}>
            <Typography variant="h6" color="primary">
              {formatCurrency(estadisticas.total)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Gastos
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#f5f5f5" }}>
            <Typography variant="h6" color="primary">
              {estadisticas.cantidad}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Transacciones
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#f5f5f5" }}>
            <Typography variant="h6" color="primary">
              {estadisticas.categorias}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Categorías
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#f5f5f5" }}>
            <Typography variant="h6" color="primary">
              {estadisticas.subcategorias}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Subcategorías
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#f5f5f5" }}>
            <Typography variant="h6" color="primary">
              {formatCurrency(estadisticas.promedio)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Promedio
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Paper sx={{ p: 2, textAlign: "center", backgroundColor: "#f5f5f5" }}>
            <Typography variant="h6" color="primary">
              {formatCurrency(estadisticas.maximo)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Máximo
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Gráfica por CATEGORÍA */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Gastos por Categoría
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={datosPorCategoria}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={80} fontSize={12} />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={(value) => [formatCurrency(value), "Total"]} />
                <Legend />
                <Bar dataKey="total" fill="#5b5b5f" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gráfica por SUBCATEGORÍA */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Gastos por Subcategoría
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={datosPorSubcategoria.slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={80} fontSize={12} />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={(value) => [formatCurrency(value), "Total"]} />
                <Legend />
                <Bar dataKey="total" fill="rgb(244, 177, 14)" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gráfica circular por CATEGORÍA */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Distribución por Categoría
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={datosPorCategoria}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ nombre, percent }) => `${nombre} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="total"
                >
                  {datosPorCategoria.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatCurrency(value), "Total"]} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gráfica por Entidad */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top Entidades por Gasto
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={datosPorEntidad}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={80} fontSize={12} />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={(value) => [formatCurrency(value), "Total"]} />
                <Legend />
                <Bar dataKey="total" fill="#82ca9d" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gráfica temporal */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Evolución Temporal de Gastos
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={datosTemporal}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="fecha"
                  tickFormatter={(value) => {
                    const date = new Date(value)
                    return `${date.getDate()}/${date.getMonth() + 1}`
                  }}
                />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip
                  formatter={(value) => [formatCurrency(value), "Total"]}
                  labelFormatter={(value) => `Fecha: ${value}`}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="rgb(244, 177, 14)"
                  strokeWidth={2}
                  dot={{ fill: "rgb(244, 177, 14)" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Comparación Categoría vs Subcategoría */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top 10: Categoría - Subcategoría
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={comparacionCategoriaSubcategoria} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={formatCurrency} />
                <YAxis dataKey="nombre" type="category" width={200} fontSize={11} />
                <Tooltip formatter={(value) => [formatCurrency(value), "Total"]} />
                <Legend />
                <Bar dataKey="monto" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Cantidad de transacciones por categoría */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Cantidad de Transacciones por Categoría
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={datosPorCategoria}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={80} fontSize={12} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="cantidad" fill="#ff7300" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Promedio por categoría */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Promedio por Categoría
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={datosPorCategoria.map((item) => ({
                  ...item,
                  promedio: item.total / item.cantidad,
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={80} fontSize={12} />
                <YAxis tickFormatter={formatCurrency} />
                <Tooltip formatter={(value) => [formatCurrency(value), "Promedio"]} />
                <Legend />
                <Bar dataKey="promedio" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}
